import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Paper,
  CircularProgress,
  Chip,
  Snackbar,
  Stack,
  Fab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Mic as MicIcon,
  DeleteOutline as DeleteOutlineIcon,
  FiberManualRecord as RecordIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { formatDate, formatPhone, formatTime as formatTimeFor } from '../utils/formatters';
import { customerService } from '../services/api';
import { useConversation } from '../contexts/ConversationContext';
import ChatMessage from '../components/ChatMessage';
import LeadActionsMenu from '../components/LeadActionsMenu';
import TransferLeadDialog from '../components/TransferLeadDialog';
import { transformConversation } from './Leads/utils/leadTransform';
import { useReleaseLead, useCloseLead, useReopenLead } from './Leads/hooks/useLeadsQuery';
import { useVoiceRecorder, isVoiceRecordingSupported } from '../hooks/useVoiceRecorder';


const INPUT_PROPS = {
  sx: {
    borderRadius: 3,
    bgcolor: 'action.hover',
  },
};

const formatDuration = (totalSeconds) => {
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

// Document formats WhatsApp delivers, keyed by extension: some pickers leave
// file.type empty for office files. customer_service relabels them as
// application/pdf for whatsapp_service, which only whitelists that one.
const DOCUMENT_MIME_TYPES = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
};
// Mirrors the media MIME types whatsapp_service accepts; anything else is rejected upstream.
const ATTACHMENT_MIME_TYPES = ['image/jpeg', 'image/png', 'video/mp4', 'video/3gpp', ...Object.values(DOCUMENT_MIME_TYPES)];
const ATTACHMENT_ACCEPT = [
  ...ATTACHMENT_MIME_TYPES,
  ...Object.keys(DOCUMENT_MIME_TYPES).map((extension) => `.${extension}`),
].join(',');

const attachmentMimeType = (file) =>
  file.type || DOCUMENT_MIME_TYPES[file.name.split('.').pop().toLowerCase()] || '';
// WhatsApp per-type upload limits, in MB.
const MAX_ATTACHMENT_MB = { image: 5, video: 16, document: 100 };

const attachmentKind = (mimeType) => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return 'document';
};

const formatFileSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} kB`;

// Module level (not a hook dependency): reads the current language when called.
const formatTime = (timestamp) => formatTimeFor(timestamp, i18n.language, { empty: '' });

const ChatConversation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageRefs = useRef({});
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  // scrollHeight do container capturado logo antes de carregar mensagens antigas,
  // usado para reancorar o scroll e evitar o "pulo" ao prepender uma página acima.
  const prevScrollHeightRef = useRef(null);

  const [replyingTo, setReplyingTo] = useState(null);
  const [copied, setCopied] = useState(false);

  const recorder = useVoiceRecorder();
  // Translated error shown below the composer (recording / attachment), or null
  const [composerError, setComposerError] = useState(null);

  // Single attachment: whatsapp_service sends one media per message.
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);

  // Image previews use object URLs, which must be released when replaced/cleared.
  useEffect(() => () => {
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
  }, [attachment]);

  const topObserverTarget = useRef(null);

  // Optimistic messages (not yet confirmed by server)
  const [optimisticMessages, setOptimisticMessages] = useState([]);

  const {
    conversation,
    latestMessages: latestMessagesRaw,
    olderPages: olderPagesRaw,
    loading: contextLoading,
    loadingOlder,
    hasOlderMessages,
    load,
    loadOlderMessages,
    reloadMessages,
    poll,
  } = useConversation();

  const canSendMessages = conversation?.authorization?.capabilities?.can_send_messages ?? false;

  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  // Ação em curso (release/close/reopen) → spinner no item do menu.
  const [pendingAction, setPendingAction] = useState(null);
  const releaseMutation = useReleaseLead();
  const closeMutation = useCloseLead();
  const reopenMutation = useReopenLead();

  // Liberar, encerrar ou reabrir tira o colaborador desta conversa — volta à lista.
  const runAndLeave = (action, mutation) => () => {
    setPendingAction(action);
    mutation.mutate(id, { onSettled: () => navigate('/leads', { replace: true }) });
  };

  // Load on screen entry
  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id, load]);

  // Polling every 6s (paused while optimistic messages are pending)
  useEffect(() => {
    if (optimisticMessages.length > 0) return;
    const interval = setInterval(poll, 6000);
    return () => clearInterval(interval);
  }, [poll, optimisticMessages]);

  const transformMessage = useCallback((msg, contactName) => {
    let replyToData = null;
    if (msg.reply_to) {
      replyToData = {
        id: msg.reply_to.message_uuid || msg.reply_to.uuid,
        message_uuid: msg.reply_to.message_uuid || msg.reply_to.uuid,
        wa_message_id: msg.reply_to.wa_message_id,
        text: msg.reply_to.text,
        name: msg.reply_to.direction === 'OUT' ? 'Você' : contactName,
      };
    }

    // OUT messages from collaborators arrive as "Nome\nmensagem".
    // Split so we can render the name as a bold header (like WhatsApp).
    // Messages without a newline (e.g. bot/AI messages) have no senderName.
    let senderName = null;
    let bodyText = msg.text || '';
    if (msg.direction === 'OUT' && msg.text && msg.text.includes('\n')) {
      const firstBreak = msg.text.indexOf('\n');
      senderName = msg.text.slice(0, firstBreak).trim();
      bodyText = msg.text.slice(firstBreak + 1);
    }

    return {
      id: msg.uuid,
      uuid: msg.uuid,
      text: bodyText,
      senderName,
      timestamp: formatTime(msg.event_at),
      fullTimestamp: msg.event_at,
      event_at: msg.event_at,
      isSent: msg.direction === 'OUT',
      direction: msg.direction,
      message_type: msg.message_type,
      has_media: msg.has_media,
      sender: msg.sender,
      media: msg.media,
      contact: msg.contact,
      reply_to: msg.reply_to,
      replyTo: replyToData,
    };
  }, []);

  // Auto-scroll on initial load
  useEffect(() => {
    if (shouldAutoScroll && messagesEndRef.current && !contextLoading) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [latestMessagesRaw, shouldAutoScroll, contextLoading]);

  // Intersection observer for loading older messages
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasOlderMessages && !loadingOlder) {
          // Captura a altura atual antes de prepender a página antiga,
          // para reancorar o scroll no useLayoutEffect abaixo.
          const container = messagesContainerRef.current;
          if (container) prevScrollHeightRef.current = container.scrollHeight;
          loadOlderMessages();
        }
      },
      { threshold: 0.1 }
    );

    const target = topObserverTarget.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasOlderMessages, loadingOlder, loadOlderMessages]);

  // Track scroll position to determine auto-scroll.
  // Depende de contextLoading: o container de mensagens só existe depois que o
  // loading termina, então o listener precisa ser (re)anexado nesse momento —
  // com deps [] ele ficava preso a um container morto e shouldAutoScroll travava
  // em true, fazendo o polling puxar a tela para o fim a cada 6s.
  useEffect(() => {
    if (contextLoading) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShouldAutoScroll(distanceFromBottom < 100);
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [contextLoading]);

  // Reancora o scroll após prepender mensagens antigas, mantendo a viewport
  // na mesma mensagem em vez de "pular" quando a página antiga entra no DOM.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || prevScrollHeightRef.current == null) return;
    const delta = container.scrollHeight - prevScrollHeightRef.current;
    if (delta > 0) container.scrollTop += delta;
    prevScrollHeightRef.current = null;
  }, [olderPagesRaw]);

  // Combine older pages + latest page + optimistic messages
  const contactName = conversation?.contact?.display_name || '';
  const olderMessages = olderPagesRaw.flat().map(msg => transformMessage(msg, contactName));
  const latestMessages = latestMessagesRaw.map(msg => transformMessage(msg, contactName));

  // Mensagens otimistas são identificadas pelo ID temporário (temp-*).
  // Elas ficam na lista até serem explicitamente removidas após reloadMessages() concluir.
  // Não tentamos deduplicar por texto — o servidor pode formatar diferente do que enviamos.
  const allMessages = [...olderMessages, ...latestMessages, ...optimisticMessages];

  const loading = contextLoading;


  const formatDateDivider = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    if (messageDate.getTime() === todayDate.getTime()) {
      return t('chat.today');
    } else if (messageDate.getTime() === yesterdayDate.getTime()) {
      return t('chat.yesterday');
    } else {
      return formatDate(date, i18n.language);
    }
  };

  const shouldShowDateDivider = (currentMsg, previousMsg) => {
    if (!previousMsg) return true;
    const currentDate = new Date(currentMsg.fullTimestamp);
    const previousDate = new Date(previousMsg.fullTimestamp);
    return currentDate.toDateString() !== previousDate.toDateString();
  };

  // Mesma pessoa nas duas mensagens: contato é sempre o mesmo remetente;
  // enviadas comparam pelo nome (não há um id estável de colaborador aqui).
  // Simétrica (não importa a ordem dos argumentos).
  const isSameSender = (msgA, msgB) => {
    if (!msgA || !msgB) return false;
    if (msgA.isSent !== msgB.isSent) return false;
    return !msgA.isSent || msgA.senderName === msgB.senderName;
  };

  const handleBack = () => {
    if (location.state?.from) return navigate(location.state.from);
    navigate('/leads');
  };

  const handleProfileClick = () => {
    if (!conversation?.contact?.uuid) return;
    if (isDesktop) {
      navigate(`/clients/${conversation.contact.uuid}`, { state: { from: location.pathname } });
    } else {
      navigate(`/customer-details/${conversation.contact.uuid}`, { state: { from: location.pathname } });
    }
  };

  // Mensagem otimista: bubble com flag pending=true (ícone de relógio em vez
  // de horário, padrão WhatsApp). Removida pelo id após reloadMessages()
  // concluir — sem deduplicação por texto.
  const buildOptimisticMessage = (fields, replyingToData) => ({
    id: `temp-${Date.now()}`,
    timestamp: null,
    fullTimestamp: new Date().toISOString(),
    isSent: true,
    pending: true,
    direction: 'OUT',
    replyTo: replyingToData ? {
      id: replyingToData.id,
      text: replyingToData.text,
      name: replyingToData.name,
    } : null,
    ...fields,
  });

  // Caminho único de envio (texto e áudio): otimista → POST → reload → remove otimista.
  const submitReply = async (payload, optimisticMsg) => {
    setOptimisticMessages(prev => [...prev, optimisticMsg]);
    setShouldAutoScroll(true);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);

    try {
      setSending(true);
      await customerService.replyMessage(id, payload);
      // Aguarda o reload terminar antes de remover a otimista,
      // evitando o flash de some-e-volta durante a transição.
      await reloadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setOptimisticMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || sending) return;

    const messageText = message.trim();
    const replyingToData = replyingTo;

    setMessage('');
    setReplyingTo(null);

    const payload = new FormData();
    payload.append('text', messageText);
    if (replyingToData) {
      payload.append('reply_to', replyingToData.id);
    }

    await submitReply(payload, buildOptimisticMessage({ text: messageText }, replyingToData));
  };

  const handleStartRecording = async () => {
    if (!isVoiceRecordingSupported()) {
      setComposerError(t('chat.recordingUnsupported'));
      return;
    }

    try {
      await recorder.start();
    } catch (error) {
      console.error('Error starting recording:', error);
      setComposerError(t(error?.name === 'NotAllowedError' ? 'chat.micPermissionDenied' : 'chat.recordingFailed'));
    }
  };

  const handlePickAttachment = (event) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again after removing it fires onChange.
    event.target.value = '';
    if (!file) return;

    const mimeType = attachmentMimeType(file);
    if (!ATTACHMENT_MIME_TYPES.includes(mimeType)) {
      setComposerError(t('chat.attachmentUnsupported'));
      return;
    }

    const kind = attachmentKind(mimeType);
    const maxMb = MAX_ATTACHMENT_MB[kind];
    if (file.size > maxMb * 1024 * 1024) {
      setComposerError(t('chat.attachmentTooLarge', { max: maxMb }));
      return;
    }

    setAttachment({
      file,
      kind,
      mimeType,
      previewUrl: kind === 'image' ? URL.createObjectURL(file) : null,
    });
  };

  const handleSendAttachment = async () => {
    if (!attachment || sending) return;

    const { file, kind, mimeType } = attachment;
    const caption = message.trim();
    const replyingToData = replyingTo;

    setAttachment(null);
    setMessage('');
    setReplyingTo(null);

    const payload = new FormData();
    payload.append('media', file, file.name);
    payload.append('mime_type', mimeType);
    if (caption) {
      payload.append('text', caption);
    }
    if (replyingToData) {
      payload.append('reply_to', replyingToData.id);
    }

    const optimisticMsg = buildOptimisticMessage({
      text: '',
      message_type: kind,
      has_media: true,
      media: { status: 'pending', mime_type: mimeType, filename: file.name, caption },
    }, replyingToData);

    await submitReply(payload, optimisticMsg);
  };

  // Text field + send button route here: an attached file wins over plain text.
  const handleSend = () => (attachment ? handleSendAttachment() : handleSendMessage());

  const handleSendAudio = async () => {
    const blob = await recorder.stop();
    if (!blob?.size || sending) return;

    // O navegador anexa ";codecs=opus"; o backend quer só o tipo base.
    const mimeType = blob.type.split(';')[0] || 'audio/webm';
    const extension = mimeType.split('/')[1] || 'webm';
    const replyingToData = replyingTo;

    setReplyingTo(null);

    const payload = new FormData();
    payload.append('media', blob, `voice-${new Date().getTime()}.${extension}`);
    payload.append('mime_type', mimeType);

    // media.status 'pending' faz AudioMessageContent renderizar o skeleton
    // sem tentar buscar a URL assinada de um uuid temporário.
    const optimisticMsg = buildOptimisticMessage({
      text: '',
      message_type: 'audio',
      has_media: true,
      media: { status: 'pending', mime_type: mimeType, is_voice_message: true },
    }, replyingToData);

    await submitReply(payload, optimisticMsg);
  };

  const scrollToMessage = (messageId) => {
    const messageElement = messageRefs.current[messageId];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageElement.style.transition = 'background-color 0.3s';
      const originalBg = messageElement.style.backgroundColor;
      messageElement.style.backgroundColor = theme.alpha((theme.vars || theme).palette.primary.main, 0.15);
      setTimeout(() => {
        messageElement.style.backgroundColor = originalBg;
      }, 1000);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const shortenName = (name) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length <= 2) return name;
    return `${parts[0]} ${parts[parts.length - 1]}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const customer = conversation?.contact || {};
  const lead = conversation?.contact ? transformConversation(conversation) : null;

  return (
    <Box
      sx={(theme) => ({
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        // DESKTOP LAYOUT: fill the right panel height instead of full viewport
        [theme.breakpoints.up('md')]: {
          height: '100%',
        },
      })}
    >
      <Paper
        elevation={1}
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.paper',
          zIndex: 10,
        }}
      >
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>

        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, cursor: 'pointer' }}
          onClick={handleProfileClick}
        >
          {/* TODO v1: online badge on avatar — hidden for v0 */}
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              width: 40,
              height: 40,
            }}
          >
            {getInitials(customer.display_name)}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2">
              {shortenName(customer.display_name)}
            </Typography>
            {/* TODO v1: online status — hidden for v0
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              ● {t('chat.online')}
            </Typography>
            */}
            {customer.phone_number && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {formatPhone(customer.phone_number)}
              </Typography>
            )}
          </Box>
        </Box>

        {lead && (
          <LeadActionsMenu
            lead={lead}
            variant={isDesktop ? 'inline' : 'menu'}
            pendingAction={pendingAction}
            disabled={Boolean(pendingAction)}
            onRelease={runAndLeave('release', releaseMutation)}
            onClose={runAndLeave('close', closeMutation)}
            onReopen={runAndLeave('reopen', reopenMutation)}
            onTransfer={() => setTransferDialogOpen(true)}
          />
        )}
      </Paper>

      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          // Fundo neutro da conversa (o da página é azulado): o papel com 4% de
          // preto, que escurece nos dois esquemas (#F5F5F5 no light, #111 no
          // dark). O `action.hover` é branco no dark e deixava a área mais clara
          // que o balão recebido. `color-mix` porque o `theme.alpha` com CSS vars
          // precisa de um canal, e o MUI não gera um para o `common.black`.
          bgcolor: (theme) => {
            const { background, common } = (theme.vars || theme).palette;
            return `color-mix(in srgb, ${background.paper} 96%, ${common.black})`;
          },
          // O espaçamento entre mensagens vem só do `mb` de cada ChatMessage
          // (menor quando agrupada com a anterior do mesmo remetente) — um gap
          // fixo aqui somaria em cima disso e atrapalharia o agrupamento.
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : allMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chat.noMessagesYet')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Loading indicator at top for older messages */}
            {hasOlderMessages && (
              <Box ref={topObserverTarget} sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {allMessages.map((msg, index) => {
              const previousMsg = index > 0 ? allMessages[index - 1] : null;
              const nextMsg = index < allMessages.length - 1 ? allMessages[index + 1] : null;
              const showDateDivider = shouldShowDateDivider(msg, previousMsg);
              // `grouped` controla o espaço DEPOIS desta mensagem (o mb fica
              // nela mesma), então compara com a PRÓXIMA, não com a anterior —
              // senão o espaçamento fica defasado em uma mensagem.
              const dateDividerBeforeNext = nextMsg && shouldShowDateDivider(nextMsg, msg);
              const grouped = Boolean(nextMsg) && !dateDividerBeforeNext && isSameSender(msg, nextMsg);
              return (
                <React.Fragment key={msg.id}>
                  {/* Date divider */}
                  {showDateDivider && (
                    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                      <Chip
                        label={formatDateDivider(msg.fullTimestamp)}
                        size="small"
                        sx={{
                          mx: 2,
                          bgcolor: 'action.selected',
                          color: 'text.secondary',
                        }}
                      />
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    </Box>
                  )}
                  <ChatMessage
                    msg={msg}
                    messageRef={(el) => (messageRefs.current[msg.id] = el)}
                    scrollToMessage={scrollToMessage}
                    // Seleção de mensagens temporariamente indisponível; deve voltar (ver mobile/README.md).
                    isSelected={false}
                    selectionMode={false}
                    onCopied={() => setCopied(true)}
                    grouped={grouped}
                  />
                </React.Fragment>
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {canSendMessages && (
        <>
          {replyingTo && (
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                bgcolor: 'background.default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  pl: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    display: 'block',
                    mb: 0.3,
                  }}
                >
                  {replyingTo.isSent ? 'Você' : conversation?.contact?.display_name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {replyingTo.text}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setReplyingTo(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          )}

          {attachment && (
            <Paper elevation={1} square>
              <Stack direction="row" spacing={1.5} alignItems="center" p={1.5}>
                <Avatar variant="rounded" src={attachment.previewUrl ?? undefined}>
                  {attachment.kind === 'video' ? <VideocamIcon /> : <InsertDriveFileIcon />}
                </Avatar>
                <Stack flex={1} minWidth={0}>
                  <Typography variant="body2" noWrap>
                    {attachment.file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(attachment.file.size)}
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  aria-label={t('chat.removeAttachment')}
                  onClick={() => setAttachment(null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          )}

          <Paper
            elevation={3}
            sx={{
              p: 1.5,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            {!recorder.recording && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  hidden
                  onChange={handlePickAttachment}
                />
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={t('chat.attachFile')}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending || Boolean(attachment)}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </>
            )}

            {recorder.recording ? (
              <Stack direction="row" spacing={1} alignItems="center" flex={1} minWidth={0}>
                <IconButton
                  size="small"
                  aria-label={t('chat.cancelRecording')}
                  onClick={recorder.cancel}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
                <RecordIcon color="error" fontSize="small" />
                <Typography variant="body2">
                  {formatDuration(recorder.seconds)}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {t('chat.recording')}
                </Typography>
              </Stack>
            ) : (
              <TextField
                fullWidth
                placeholder={t(attachment ? 'chat.captionPlaceholder' : 'chat.messagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                size="small"
                multiline
                maxRows={3}
                InputProps={INPUT_PROPS}
              />
            )}

            {recorder.recording ? (
              <Fab
                size="small"
                color="primary"
                aria-label={t('chat.sendAudio')}
                onClick={handleSendAudio}
                disabled={sending}
              >
                <SendIcon fontSize="small" />
              </Fab>
            ) : !message.trim() && !attachment ? (
              <IconButton
                size="small"
                color="primary"
                aria-label={t('chat.recordAudio')}
                onClick={handleStartRecording}
                disabled={sending}
              >
                <MicIcon fontSize="small" />
              </IconButton>
            ) : (
              <Fab
                size="small"
                color="primary"
                aria-label={t('chat.sendMessage')}
                onClick={handleSend}
                disabled={sending}
              >
                <SendIcon fontSize="small" />
              </Fab>
            )}
          </Paper>
        </>
      )}

      {!canSendMessages && (
        <Box
          sx={{
            bgcolor: 'primary.dark',
            color: 'primary.contrastText',
            py: 1.5,
            px: 2,
            textAlign: 'center',
            typography: 'subtitle2',
          }}
        >
          {t('chat.readOnly')}
        </Box>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={t('chat.messageCopied')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Snackbar
        open={Boolean(composerError)}
        autoHideDuration={4000}
        onClose={() => setComposerError(null)}
        message={composerError ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <TransferLeadDialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        lead={lead}
        onTransferSuccess={() => navigate('/leads', { replace: true })}
      />
    </Box>
  );
};

export default ChatConversation;
